import { Box, Divider, makeStyles, Theme, Typography, useMediaQuery } from "@material-ui/core";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import CustomCard from "../../../components/CustomCard";
import ImagePreviewer from "../../../components/ImagePreviewer";
import Loader from "../../../components/Loader";
import { GenericObject } from "../../../types/generic.types";
import { clientDocRef, visitRequestDocRef } from "../../../utils/referencies";
import TitledText from "./TitledText";

const useStyles = makeStyles(() => ({
  flexBox: {
    display: "flex",
    alignItems: "center",
    marginBottom: 18,
    paddingBottom: 18,
    borderBottom: "1px solid #e0e0e0",
  },
  boxTitle: {
    minWidth: "300px",
  },
  boxTitleRespons: {
    width: "100px",
    minWidth: "100px",
  },
  imgStyle: {
    width: "150px",
  },
}));

const ShowRequest = () => {
  const classes = useStyles();
  const { visitRequestId } = useParams<{ visitRequestId: string }>();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const [fetchedClient, setClientData] = useState<GenericObject | undefined>(undefined);
  const [fetchedRequest, setFetchedRequest] = useState<GenericObject | undefined>(undefined);
  const [fetchedPlace, setFetchedPlace] = useState<GenericObject | undefined>(undefined);
  const [loadingRequestData, setLoadingRequestData] = useState(false);
  const [loadingClientData, setLoadingClientData] = useState(false);
  const [loadingPlaceData, setLoadingPlaceData] = useState(false);

  /**
   * this function to get visit request details
   */
  const getRequestData = async () => {
    setLoadingRequestData(true);
    try {
      const snapshot = await visitRequestDocRef(visitRequestId).get();
      if (snapshot.exists) {
        const fetchedRequest: GenericObject = {
          ...snapshot.data(),
          id: snapshot.id,
        };
        setFetchedRequest(fetchedRequest);
        setLoadingRequestData(false);
      }
    } catch (ex: any) {
      console.log(ex.message);
      setLoadingRequestData(false);
    }
  };

  /**
   * get place info
   */
  const getPlaceInfo = async (placeRef: any) => {
    setLoadingPlaceData(true);
    try {
      const snapshot = await placeRef.get();
      const fetchedPlace: GenericObject = {
        ...snapshot.data(),
        id: snapshot.id,
      };
      setFetchedPlace(fetchedPlace);
      setLoadingPlaceData(false);
    } catch (ex: any) {
      console.log(ex.message);
      setLoadingPlaceData(false);
    }
  };

  /**
   * this function to get client data who create request
   */
  const getClientData = async (clientId: string) => {
    setLoadingClientData(true);
    try {
      const snapshot = await clientDocRef(clientId).get();
      setClientData(snapshot.data());
      setLoadingClientData(false);
    } catch (ex: any) {
      console.log(ex.message);
      setLoadingClientData(false);
    }
  };

  /**
   * fetch visit request data
   */
  useEffect(() => {
    getRequestData();
  }, []);

  useEffect(() => {
    if (fetchedRequest) {
      getClientData(fetchedRequest.clientId);
      getPlaceInfo(fetchedRequest.placeRef);
    }
  }, [fetchedRequest]);

  return (
    <Loader loading={loadingRequestData}>
      <CustomCard width="50%">
        {fetchedRequest ? (
          <>
            <Box className={classes.flexBox}>
              <Typography className={isSm ? classes.boxTitleRespons : classes.boxTitle}>Request</Typography>
              <Box>
                <TitledText title="Status" text={fetchedRequest.status} />
                <TitledText title="Purpose of visit" text={fetchedRequest.purposeOfVisit} />
                <TitledText
                  title="Visiting date"
                  text={dayjs(fetchedRequest.visitingDate.toDate()).format("DD/MM/YYYY")}
                />
              </Box>
            </Box>

            <Box className={classes.flexBox}>
              <Typography className={isSm ? classes.boxTitleRespons : classes.boxTitle}>Place</Typography>
              <Loader loading={loadingPlaceData}>
                {fetchedPlace ? (
                  <Box>
                    <TitledText title="Title" text={fetchedPlace.title.en} />
                    <TitledText title="Price" text={fetchedPlace.price} />
                    <TitledText
                      title="Address"
                      text={`${fetchedPlace.address.city}, ${fetchedPlace.address.country.en}, ${fetchedPlace.address.details.en}`}
                    />
                    <TitledText
                      title="Profile image"
                      text={<ImagePreviewer src={fetchedPlace.profileImage.src} alt={fetchedPlace.title.en} />}
                    />
                  </Box>
                ) : null}
              </Loader>
            </Box>

            <Box className={classes.flexBox}>
              <Typography className={isSm ? classes.boxTitleRespons : classes.boxTitle}>Client</Typography>
              <Loader loading={loadingClientData}>
                {fetchedClient ? (
                  <Box>
                    <TitledText title="Name" text={fetchedClient.displayName} />
                    <TitledText title="Email" text={fetchedClient.email || "N/A"} />
                    <TitledText title="Phone" text={fetchedClient.phoneNumber || "N/A"} />
                  </Box>
                ) : null}
              </Loader>
            </Box>

            <Box className={classes.flexBox}>
              <Typography className={isSm ? classes.boxTitleRespons : classes.boxTitle}>Visitors</Typography>
              <Loader loading={loadingClientData}>
                {fetchedClient ? (
                  <Box>
                    {fetchedRequest.visitors.map((visitor: GenericObject) => {
                      return (
                        <>
                          <TitledText title="Name" text={visitor.name} />
                          <TitledText title="Phone" text={visitor.phone} />
                          <TitledText title="National ID" text={visitor.nationalId} />
                          <TitledText
                            title="National ID photo"
                            text={<ImagePreviewer src={visitor.nationalIdPhoto.src} alt={visitor.name} />}
                          />
                          <Divider />
                        </>
                      );
                    })}
                  </Box>
                ) : null}
              </Loader>
            </Box>
          </>
        ) : (
          <Typography variant="body1">Request does not exist</Typography>
        )}
      </CustomCard>
    </Loader>
  );
};

export default ShowRequest;
